import React, { useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import { Download, FileUp, Lock, Upload } from "lucide-react";
import { useAttendanceCodeConfig } from "../../../../context/attendanceCodeConfigContext";
import Modal from "../../masters/Modal";
import { usePermissions } from "../../../../rbac/PermissionsContext";

const DEFAULT_CONFIGS = [
  { meaning: "Present", originalCode: "PR", customCode: "PR", importCode: "PR", exportCode: "PR" },
  { meaning: "Loss of Pay", originalCode: "LOP", customCode: "LOP", importCode: "LOP", exportCode: "LOP" },
  { meaning: "Weekly Off", originalCode: "WO", customCode: "WO", importCode: "WO", exportCode: "WO" },
  { meaning: "Public Holiday", originalCode: "PH", customCode: "PH", importCode: "PH", exportCode: "PH" },
];

function mapConfigRow(c) {
  return {
    meaning: c.meaning || "",
    originalCode: (c.originalCode || c.original || c.original_code || "").toUpperCase(),
    customCode: (
      c.customCode || c.custom || c.custom_code || c.originalCode || ""
    ).toUpperCase(),
    importCode: (c.importCode || c.import_code || c.originalCode || "").toUpperCase(),
    exportCode: (c.exportCode || c.export_code || c.originalCode || "").toUpperCase(),
  };
}

function parseCodesCsv(text) {
  const lines = String(text || "")
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter(Boolean);
  if (lines.length < 2) return [];
  const header = lines[0].toLowerCase();
  const hasHeader =
    header.includes("meaning") ||
    header.includes("default") ||
    header.includes("import");
  const dataLines = hasHeader ? lines.slice(1) : lines;
  const rows = [];
  for (const line of dataLines) {
    const cols = line.split(",").map((c) => c.replace(/^"|"$/g, "").trim());
    if (cols.length < 4) continue;
    const [meaning, originalCode, importCode, exportCode] = cols;
    const orig = (originalCode || importCode || "").toUpperCase();
    if (!orig) continue;
    rows.push({
      meaning: meaning || orig,
      originalCode: orig,
      customCode: orig,
      importCode: (importCode || orig).toUpperCase(),
      exportCode: (exportCode || orig).toUpperCase(),
    });
  }
  return rows;
}

/**
 * Import / export attendance code mapping (default PR, LOP, WO, PH + custom rows).
 */
const AttendanceCodeMappingPanel = ({ darkMode }) => {
  const {
    loading,
    error,
    fetchConfigurations,
    saveConfigurations,
    resetConfigurations,
    halfDayDivider,
    halfDayPrefix,
  } = useAttendanceCodeConfig();
  const [rows, setRows] = useState([]);
  const [saving, setSaving] = useState(false);
  const [showResetModal, setShowResetModal] = useState(false);
  const [halfDayDividerLocal, setHalfDayDividerLocal] = useState(halfDayDivider ?? "");
  const [halfDayPrefixLocal, setHalfDayPrefixLocal] = useState(halfDayPrefix ?? "");
  const importInputRef = useRef(null);

  const { hasScreenAccess, isAdmin } = usePermissions();
  const canEdit =
    isAdmin || hasScreenAccess("configuration", "attendance_codes", "edit");
  const canExport =
    isAdmin || hasScreenAccess("configuration", "attendance_codes", "export");
  const canView =
    isAdmin || hasScreenAccess("configuration", "attendance_codes", "view");

  useEffect(() => {
    setHalfDayDividerLocal(
      halfDayDivider != null && halfDayDivider !== "" ? halfDayDivider : ""
    );
    setHalfDayPrefixLocal(
      halfDayPrefix != null && halfDayPrefix !== "" ? halfDayPrefix : ""
    );
  }, [halfDayDivider, halfDayPrefix]);

  useEffect(() => {
    const load = async () => {
      const configs = await fetchConfigurations();
      const codesArray = Array.isArray(configs)
        ? configs
        : configs?.codes || configs?.configurations || [];
      if (!codesArray?.length) {
        setRows(DEFAULT_CONFIGS.map(mapConfigRow));
      } else {
        setRows(codesArray.map(mapConfigRow));
      }
    };
    if (canView) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canView]);

  const handleChange = (idx, field, value) => {
    setRows((prev) => {
      const next = [...prev];
      next[idx] = { ...next[idx], [field]: String(value || "").toUpperCase() };
      return next;
    });
  };

  const handleSave = async () => {
    if (!canEdit) {
      toast.error("You do not have permission to save attendance codes.");
      return;
    }
    try {
      setSaving(true);
      const codes = rows.map((r) => ({
        meaning: r.meaning,
        customCode: (r.customCode || r.originalCode || "").toUpperCase(),
        importCode: (r.importCode || "").toUpperCase(),
        exportCode: (r.exportCode || "").toUpperCase(),
      }));
      for (const c of codes) {
        if (!c.importCode) {
          toast.error(`Import code for "${c.meaning}" cannot be empty`);
          setSaving(false);
          return;
        }
        if (!c.exportCode) {
          toast.error(`Export code for "${c.meaning}" cannot be empty`);
          setSaving(false);
          return;
        }
      }
      const resp = await saveConfigurations({
        codes,
        halfDayDivider:
          halfDayDividerLocal.trim() === "" ? null : halfDayDividerLocal.trim(),
        halfDayPrefix:
          halfDayPrefixLocal.trim() === "" ? null : halfDayPrefixLocal.trim(),
      });
      const saved = Array.isArray(resp)
        ? resp
        : resp?.data ?? resp?.codes ?? resp?.configurations ?? [];
      if (saved?.length) setRows(saved.map(mapConfigRow));
      else {
        const fresh = await fetchConfigurations();
        setRows((fresh || DEFAULT_CONFIGS).map(mapConfigRow));
      }
      toast.success("Code mapping saved");
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const confirmReset = async () => {
    setShowResetModal(false);
    if (!canEdit) return;
    try {
      setSaving(true);
      const resp = await resetConfigurations();
      const configs = Array.isArray(resp)
        ? resp
        : resp?.data ?? resp?.codes ?? resp?.configurations ?? [];
      setRows(configs.map(mapConfigRow));
      toast.success("Reset to default codes");
    } catch (err) {
      toast.error(err?.message || "Reset failed");
    } finally {
      setSaving(false);
    }
  };

  const exportCsv = () => {
    if (!canExport) {
      toast.error("You do not have permission to export.");
      return;
    }
    const headers = ["Meaning", "Default Code", "Import Code", "Export Code"];
    const escape = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const lines = [
      headers.join(","),
      ...rows.map((r) =>
        [r.meaning, r.originalCode, r.importCode, r.exportCode].map(escape).join(",")
      ),
    ];
    const blob = new Blob([lines.join("\r\n")], { type: "text/csv;charset=utf-8;" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "attendance_code_mapping.csv";
    a.click();
    URL.revokeObjectURL(a.href);
    toast.success("Exported code mapping");
  };

  const downloadTemplate = () => {
    if (!canExport) return;
    const headers = ["Meaning", "Default Code", "Import Code", "Export Code"];
    const sample = ["Present", "PR", "PR", "PR"];
    const blob = new Blob([[headers.join(","), sample.join(",")].join("\r\n")], {
      type: "text/csv;charset=utf-8;",
    });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "attendance_codes_import_template.csv";
    a.click();
    URL.revokeObjectURL(a.href);
    toast.success("Template downloaded");
  };

  const onImportFile = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !canEdit) return;
    const reader = new FileReader();
    reader.onload = () => {
      const parsed = parseCodesCsv(reader.result);
      if (!parsed.length) {
        toast.error("No valid rows found in CSV");
        return;
      }
      setRows(parsed);
      toast.success(`Loaded ${parsed.length} code row(s). Click Save to apply.`);
    };
    reader.readAsText(file);
  };

  if (!canView) {
    return (
      <p className={`text-sm ${darkMode ? "text-gray-400" : "text-gray-600"}`}>
        You do not have permission to view attendance code mapping.
      </p>
    );
  }

  if (loading && rows.length === 0) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="h-10 w-10 animate-spin rounded-full border-b-2 border-redhalo-red" />
      </div>
    );
  }

  const border = darkMode ? "border-gray-600" : "border-gray-200";
  const inputCls = `w-full max-w-[8rem] px-2 py-1.5 border rounded text-sm ${
    darkMode ? "bg-gray-800 text-white border-gray-600" : "bg-white border-gray-300"
  }`;

  return (
    <div className="space-y-4">
      {error && (
        <div className="rounded border border-red-400 bg-red-100 p-3 text-sm text-red-700">
          {error?.message || String(error)}
        </div>
      )}

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h4 className={`text-base font-semibold ${darkMode ? "text-white" : "text-gray-900"}`}>
            Import & export code mapping
          </h4>
          <p className={`mt-1 text-xs ${darkMode ? "text-gray-400" : "text-gray-500"}`}>
            Map default attendance codes (PR, LOP, WO, PH) to your import/export file
            formats. Used across attendance grid, calendar, and Excel import/export.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <input
            ref={importInputRef}
            type="file"
            accept=".csv,text/csv"
            className="hidden"
            onChange={onImportFile}
          />
          <button
            type="button"
            disabled={!canEdit}
            onClick={() => importInputRef.current?.click()}
            className="inline-flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-sm hover:bg-gray-50 dark:hover:bg-gray-800"
          >
            <FileUp className="h-4 w-4" />
            Import CSV
          </button>
          <button
            type="button"
            disabled={!canExport || rows.length === 0}
            onClick={exportCsv}
            className="inline-flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-sm hover:bg-gray-50 dark:hover:bg-gray-800"
          >
            <Upload className="h-4 w-4" />
            Export CSV
          </button>
          <button
            type="button"
            disabled={!canExport}
            onClick={downloadTemplate}
            className="inline-flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-sm hover:bg-gray-50 dark:hover:bg-gray-800"
          >
            <Download className="h-4 w-4" />
            Template
          </button>
        </div>
      </div>

      <div className={`rounded-lg border p-4 ${border} ${darkMode ? "bg-gray-800/40" : "bg-gray-50"}`}>
        <h5 className="text-sm font-semibold mb-2">Half-day export format</h5>
        <p className="text-xs mb-3 opacity-90">
          Example: prefix <strong>1/2</strong> and divider <strong>+</strong> →{" "}
          <strong>1/2EL+1/2ML</strong>
        </p>
        <div className="flex flex-wrap gap-4">
          <div>
            <label className="block text-xs mb-1">Divider</label>
            <input
              type="text"
              value={halfDayDividerLocal}
              onChange={(e) => setHalfDayDividerLocal(e.target.value)}
              placeholder="+"
              maxLength={10}
              className={inputCls}
              disabled={!canEdit}
            />
          </div>
          <div>
            <label className="block text-xs mb-1">Prefix (optional)</label>
            <input
              type="text"
              value={halfDayPrefixLocal}
              onChange={(e) => setHalfDayPrefixLocal(e.target.value)}
              placeholder="1/2"
              maxLength={20}
              className={inputCls}
              disabled={!canEdit}
            />
          </div>
        </div>
      </div>

      <div className="overflow-auto max-h-[50vh] rounded border border-gray-200 dark:border-gray-600">
        <table className="min-w-full table-auto border-collapse text-sm">
          <thead className={darkMode ? "bg-gray-800" : "bg-gray-100"}>
            <tr>
              <th className="p-2 text-left">Meaning</th>
              <th className="p-2 text-left">Default code</th>
              <th className="p-2 text-left">Import code</th>
              <th className="p-2 text-left">Export code</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={`${r.originalCode}-${i}`} className="border-t border-gray-200 dark:border-gray-700">
                <td className="p-2">{r.meaning}</td>
                <td className="p-2 font-mono">{r.originalCode}</td>
                <td className="p-2">
                  <input
                    value={r.importCode}
                    onChange={(e) => handleChange(i, "importCode", e.target.value)}
                    className={inputCls}
                    disabled={!canEdit}
                  />
                </td>
                <td className="p-2">
                  <input
                    value={r.exportCode}
                    onChange={(e) => handleChange(i, "exportCode", e.target.value)}
                    className={inputCls}
                    disabled={!canEdit}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <button
          type="button"
          onClick={handleSave}
          disabled={saving || !canEdit}
          className={`px-4 py-2 rh-btn-primary rounded-md ${!canEdit ? "opacity-60 cursor-not-allowed" : ""}`}
        >
          {saving ? "Saving…" : "Save mapping"}
        </button>
        <button
          type="button"
          onClick={() => setShowResetModal(true)}
          disabled={saving || !canEdit}
          className={`px-4 py-2 border rounded-md ${border}`}
        >
          Reset defaults
        </button>
        {!canEdit && (
          <span
            className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-[11px] ${
              darkMode ? "border-amber-700 text-amber-200" : "border-amber-300 text-amber-800"
            }`}
          >
            <Lock className="h-3 w-3" />
            View only
          </span>
        )}
      </div>

      <Modal
        darkMode={darkMode}
        isOpen={showResetModal}
        onClose={() => setShowResetModal(false)}
        title="Reset to defaults"
        onSubmit={confirmReset}
        submitText="Reset"
        onCancel={() => setShowResetModal(false)}
      >
        <p className="text-sm">
          Reset all import/export codes and half-day format to system defaults?
        </p>
      </Modal>
    </div>
  );
};

export default AttendanceCodeMappingPanel;
